import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AppIcon } from '@/components/ui/icons/AppIcon';
import { SitetrackerIcon } from '@/components/ui/icons/SitetrackerIcon';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/utils/helpers';
import { Bell, Loader2, LogIn, LogOut, Settings, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className: string }>;
  basePath: string;
  defaultPath?: string;
  badge?: number;
  hideWhenNotFocused?: boolean;
}

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const { t } = useTranslation();

  const menu: MenuItem[] = [];

  const activeMenu = menu.find(item => location.pathname.startsWith(item.basePath));

  const visibleMenu = menu.filter(item => !item.hideWhenNotFocused || item.id === activeMenu?.id);

  const title = activeMenu?.label || t('app.name');

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Header */}
      <header className="h-11 flex items-center justify-between pl-3.5 pr-5 flex-shrink-0">
        <div className="flex items-center gap-2.5 shrink-0">
          <AppIcon />
          <h1 className="font-semibold">{title}</h1>
        </div>
        <div className="flex items-center gap-2 w-full justify-end">
          {isAuthenticated ? (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost-gray" size="icon" className="h-8 w-8 p-0">
                    <Settings className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>{t('userMenu.language')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <LanguageSwitcher />
                </DropdownMenuContent>
              </DropdownMenu>

              <Button variant="ghost-gray" size="icon" className="h-8 w-8 p-0">
                <Bell className="h-4 w-4" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost-gray"
                    className="h-8 w-8 p-0 hover:bg-transparent"
                    disabled={isLoading}
                    title={user?.name || user?.email || ''}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Avatar className="h-8 w-8" data-testid="user-avatar">
                        <AvatarFallback className="hover:bg-gray-light-5">
                          {user?.name?.charAt(0) ||
                            user?.email?.charAt(0) ||
                            t('userMenu.unknownAvatarLetter')}
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>{t('userMenu.myAccount')}</DropdownMenuLabel>
                  {user?.email && (
                    <div className="px-2 py-1.5">
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                  )}
                  {user?.org_id && (
                    <div className="px-2 py-1.5">
                      <p className="text-xs text-muted-foreground">
                        {t('userMenu.organization', { organization: user.org_id })}
                      </p>
                    </div>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="mr-2 h-4 w-4" />
                    {t('userMenu.profile')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive focus:bg-destructive/10"
                    onClick={() => logout()}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('userMenu.logOut')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button
              variant="outline"
              onClick={() =>
                navigate(
                  `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`
                )
              }
            >
              <LogIn className="mr-2 h-4 w-4" />
              {t('userMenu.login')}
            </Button>
          )}
        </div>
      </header>

      {/* Content area with sidebar and main content */}
      <div className="flex-1 flex min-h-0 pr-5 pb-4 ">
        {/* Sidebar */}
        <aside className="flex flex-col flex-shrink-0 pt-7 pb-1 px-3.5  items-center">
          <nav className="flex-1 flex flex-col gap-3">
            {visibleMenu.map(({ id, label, icon: Icon, basePath, defaultPath, badge }) => {
              const isActive = activeMenu?.id === id;
              return (
                <Button
                  key={id}
                  variant={isActive ? 'ghost-gray-active' : 'ghost-gray'}
                  className={cn(
                    'w-8 h-8 justify-center relative rounded-lg !px-2 !py-2',
                    isActive ? '' : ''
                  )}
                  onClick={() => navigate(defaultPath || basePath)}
                  title={label}
                  aria-label={label}
                  aria-current={isActive ? 'page' : undefined}
                  data-testid={`menu-${id}-button`}
                >
                  <Icon className="w-4 h-4" />
                  {badge && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center">
                      {badge}
                    </span>
                  )}
                </Button>
              );
            })}
          </nav>
          <SitetrackerIcon size={21} />
        </aside>

        {/* Main content area */}
        <main className="flex-1 overflow-auto min-h-0 bg-white rounded-xl shadow-md">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
